import { z } from 'zod';
import { enumSchema } from '@erikdakoda/database/zodSchemas';
import { type SchemaFormat } from '@erikdakoda/utils/schemaRegistry';
import {
  ProfileFieldFromAnalysisSchema,
  buildFactoidFromAnalysisSchema,
} from '@erikdakoda/memory/shared/MemoryAnalysisSchemas';

const LifeDomainIdSchema = enumSchema('LifeDomainId');

export const MoodAndEnergySchema = z.object({
  mood: z
    .number()
    .min(0)
    .max(10)
    .nullable()
    .describe('Integer between 0 (worst possible) and 10 (best possible) mood of the user'),
  energy: z
    .number()
    .min(0)
    .max(10)
    .nullable()
    .describe('Integer between 0 (worst possible) and 10 (best possible) energy level of the user'),
  stress: z
    .number()
    .min(0)
    .max(10)
    .nullable()
    .describe('Integer between 0 (worst possible) and 10 (best possible) stress level of the user'),
  confidence: z
    .number()
    .min(0)
    .max(10)
    .nullable()
    .describe(
      'Integer between 0 (worst possible) and 10 (best possible) confidence level of the user',
    ),
  feelings: z
    .array(z.string())
    .nullable()
    .describe('An array of strings representing the feelings of the user'),
});

export const PlanningSchema = z.object({
  primaryGoal: z.string().nullable(),
  secondaryGoals: z.array(z.string()).nullable(),
  obstacles: z.array(z.string()).nullable(),
  strategiesDiscussed: z.array(z.string()).nullable(),
  habitsMentioned: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        statusOrChange: z.string(),
        scheduleOrFrequency: z.string(),
        notes: z.string(),
      }),
    )
    .nullable(),
  metricsMentioned: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        currentValue: z.string(),
        targetOrDirection: z.string(),
        notes: z.string(),
      }),
    )
    .nullable(),
  resourcesMentioned: z
    .array(
      z.object({
        id: z.string(),
        title: z.string(),
        type: z.string(),
        authorOrSource: z.string(),
        notes: z.string(),
      }),
    )
    .nullable(),
});

/** Renamed from UviloTags — taxonomy-shaped category tags for coaching analysis. */
export const UviloCategoriesSchema = z.object({
  lifeDomains: z.array(LifeDomainIdSchema).nullable(),
  issues: z.array(z.string()).nullable(),
  aspirations: z.array(z.string()).nullable(),
  practices: z.array(z.string()).nullable(),
});

/** Convo analysis payload stamped as `uvilo-ai.coaching-analysis@1`. */
export const CoachingAnalysisSchema = z.object({
  oneLiner: z.string(),
  whatWeCovered: z.array(z.string()),
  decisionsAndCommitments: z.array(z.string()).nullable(),
  openQuestions: z.array(z.string()).nullable(),
  risksOrConcerns: z.array(z.string()).nullable(),
  followUpRecommended: z.array(z.string()).nullable(),
  nextSteps: z.array(z.string()).nullable(),
  userMoodAndEnergy: MoodAndEnergySchema,
  planning: PlanningSchema.nullable(),
  uviloCategories: UviloCategoriesSchema.nullable(),
});

export type CoachingAnalysis = z.infer<typeof CoachingAnalysisSchema>;

export const coachingAnalysisFormat: SchemaFormat = {
  oneLiner: (_key: string, value: unknown) => `## One-liner\n\n${value}`,
  whatWeCovered: null,
  decisionsAndCommitments: null,
  openQuestions: null,
  risksOrConcerns: null,
  followUpRecommended: null,
  nextSteps: null,
  userMoodAndEnergy: {
    mood: (_key: string, value: unknown) => `- **Mood:** ${value} / 10`,
    energy: (_key: string, value: unknown) => `- **Energy:** ${value} / 10`,
    stress: (_key: string, value: unknown) => `- **Stress:** ${value} / 10`,
    confidence: (_key: string, value: unknown) => `- **Confidence:** ${value} / 10`,
    feelings: (_key: string, value: unknown) => {
      const feelings = Array.isArray(value) ? (value as string[]) : [];
      return `- **Feelings:** ${feelings.join(', ') || 'None'}`;
    },
  },
  planning: {
    primaryGoal: null,
    secondaryGoals: null,
    obstacles: null,
    strategiesDiscussed: null,
    habitsMentioned: {
      id: null,
      name: null,
      statusOrChange: null,
      scheduleOrFrequency: null,
      notes: null,
    },
    metricsMentioned: {
      id: null,
      name: null,
      currentValue: null,
      targetOrDirection: null,
      notes: null,
    },
    resourcesMentioned: {
      id: null,
      title: null,
      type: null,
      authorOrSource: null,
      notes: null,
    },
  },
  uviloCategories: {
    lifeDomains: (_key: string, value: unknown) => {
      const lifeDomains = Array.isArray(value) ? (value as string[]) : [];
      return `- **Life Domains:** ${lifeDomains.map((d) => `\`${d}\``).join(', ') || 'None'}`;
    },
    issues: (_key: string, value: unknown) => {
      const issues = Array.isArray(value) ? (value as string[]) : [];
      return `- **Issues:** ${issues.map((issue) => `\`${issue}\``).join(', ') || 'None'}`;
    },
    aspirations: (_key: string, value: unknown) => {
      const aspirations = Array.isArray(value) ? (value as string[]) : [];
      return `- **Aspirations:** ${aspirations.map((a) => `\`${a}\``).join(', ') || 'None'}`;
    },
    practices: (_key: string, value: unknown) => {
      const practices = Array.isArray(value) ? (value as string[]) : [];
      return `- **Practices:** ${practices.map((p) => `\`${p}\``).join(', ') || 'None'}`;
    },
  },
};

/** Analyzer bot output: embeds coaching analysis plus extractable profile/factoids. */
export const AnalyzeCoachConvoSchema = z.object({
  analysis: CoachingAnalysisSchema,
  profileFields: z.array(ProfileFieldFromAnalysisSchema),
  factoids: z.array(buildFactoidFromAnalysisSchema()),
});

export type AnalyzeCoachConvo = z.infer<typeof AnalyzeCoachConvoSchema>;
